import {
  Users,
  MessageCircle,
  ShieldCheck,
  Compass,
} from "lucide-react";

// 1. BACKGROUND IMAGE — DESKTOP ONLY (used as a full-bleed CSS background-image)
import HERO_BACKGROUND_IMAGE from "../../assets/Backgroundimage2.png";
// 2. PHONE IMAGE — MOBILE ONLY
import HERO_PHONE_BGIMAGE from "../../assets/herophone.png";

// 3. LEFT SIDE — 3 FEATURE BULLETS
const FEATURE_A_IMAGE_URL = "./img/home/FindTravelers.svg"; // Find travelers going your way
const FEATURE_B_IMAGE_URL = "./img/home/ConnectChat.svg"; // Connect, chat & plan together
const FEATURE_C_IMAGE_URL = "./img/home/TravelSafe.svg"; // Travel safe with verified profiles

// 4. BOTTOM — 4 FEATURES
const FEATURE_1_IMAGE_URL = "./img/home/Verified&Safe.svg"; // Verified & Safe
const FEATURE_2_IMAGE_URL = "./img/home/SamePlace.svg"; // Same Place, Same Dates
const FEATURE_3_IMAGE_URL = "./img/home/Connect&Plan.svg"; // Connect & Plan
const FEATURE_4_IMAGE_URL = "./img/home/Discovery.svg"; // Explore Together

function FeatureItem({ icon: Icon, imageSrc, text }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex items-center justify-center w-9 h-9 rounded-full  shrink-0 overflow-hidden">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={text}
            className="w-full h-full object-contain"
          />
        ) : (
          <Icon size={16} strokeWidth={2.5} />
        )}
      </span>
      <span className="text-gray-800 text-[15px] md:text-base font-medium">
        {text}
      </span>
    </div>
  );
}

function BottomFeature({ icon: Icon, imageSrc, title, description, isLast }) {
  return (
    <div
      className={`flex items-start gap-4 ${
        !isLast
          ? "border-b border-gray-100 pb-5 mb-5 sm:border-b-0 sm:pb-0 sm:mb-0"
          : ""
      }`}
    >
      <span className="flex items-start justify-center w-10 h-10 text-brand-purple shrink-0 overflow-hidden">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={title}
            className="w-[28px] h-[28px] object-contain"
          />
        ) : (
          <Icon size={20} strokeWidth={2.2} />
        )}
      </span>
      <div>
        <h3 className="font-bold text-gray-900 text-[14px] leading-snug">
          {title}
        </h3>
        <p className="text-gray-500 text-[12px] leading-relaxed mt-1">
          {description}
        </p>
      </div>
    </div>
  );
}

function Hero() {
  const features = [
    {
      icon: ShieldCheck,
      imageSrc: FEATURE_1_IMAGE_URL,
      title: "Verified & Safe",
      description: "All profiles are verified to ensure your safety.",
    },
    {
      icon: Users,
      imageSrc: FEATURE_2_IMAGE_URL,
      title: "Same Place, Same Dates",
      description: "Find people travelling to the same destination same dates.",
    },
    {
      icon: MessageCircle,
      imageSrc: FEATURE_3_IMAGE_URL,
      title: "Connect & Plan",
      description: "Chat, share plans and create unforgettable memories.",
    },
    {
      icon: Compass,
      imageSrc: FEATURE_4_IMAGE_URL,
      title: "Explore Together",
      description: "Discover new places and make lifelong connections.",
    },
  ];

  return (
    <section id="home" className="relative w-full pt-10">
      <div className="w-full">
        <div className="relative overflow-hidden min-h-[680px] md:min-h-[640px]">
           {/* DESKTOP ONLY  */}
          <div className="hidden md:block absolute inset-0 z-0">
            <div
              className="absolute inset-0 bg-cover bg-right bg-no-repeat"
              style={{ backgroundImage: `url(${HERO_BACKGROUND_IMAGE})` }}
            />

            {/* Fade so the photo blends into the lavender/white background behind the text */}
              <div className="absolute inset-y-0 left-0 w-[55%] bg-gradient-to-r from-white via-white/95 to-transparent pointer-events-none" />

          </div>

        {/* mobile  */}
          <div
            className="md:hidden absolute inset-0 z-0 bg-cover bg-top bg-no-repeat"
            style={{ backgroundImage: `url(${HERO_PHONE_BGIMAGE})` }}
          />

          <div className="relative z-10 px-6 md:pl-14 md:pr-0 pt-16 md:pt-28 pb-8 md:pb-16 text-center md:text-left md:w-[30%]">
            <h1 className="font-extrabold tracking-tight leading-[1.08] ">
              <span className="block whitespace-nowrap text-[7vw] sm:text-[6vw] md:text-[4.2vw] lg:text-[4vw] ">
                Travel Better.
              </span>
              <span className="block whitespace-nowrap text-[7vw] sm:text-[6vw] md:text-[4.2vw] lg:text-[4vw] text-[#7209B7]">
                Never Travel Alone.
              </span>
            </h1>

            <p className="mt-4 text-gray-600 text-[15px] md:text-base max-w-md leading-relaxed mx-auto md:mx-0">
              SoloConnect helps solo travelers find verified travel companions
              going to the same place, on the same dates.
            </p>

            <div className="mt-7 flex flex-col gap-4 items-start w-fit mx-auto md:mx-0">
              <FeatureItem
                icon={Users}
                imageSrc={FEATURE_A_IMAGE_URL}
                text="Find travelers going your way"
              />
              <FeatureItem
                icon={MessageCircle}
                imageSrc={FEATURE_B_IMAGE_URL}
                text="Connect, chat & plan together"
              />
              <FeatureItem
                icon={ShieldCheck}
                imageSrc={FEATURE_C_IMAGE_URL}
                text="Travel safe with verified profiles"
              />
            </div>
          </div>
        </div>

        {/* Bottom floating feature bar */}
        <div className="relative z-20 px-4 md:px-10 -mt-16 sm:-mt-20 md:-mt-10 pb-12 md:pb-0">
          <div className="absolute inset-x-0 bottom-0 top-16 sm:top-20 md:top-10 bg-[#f4f2fb] -z-10" />
          <div className="max-w-[1300px] mx-auto bg-white rounded-[2rem] border border-gray-100 px-6 py-8 md:px-10 md:py-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-4 lg:gap-x-2">
            {features.map((feature, idx) => (
              <BottomFeature
                key={idx}
                icon={feature.icon}
                imageSrc={feature.imageSrc}
                title={feature.title}
                description={feature.description}
                isLast={idx === features.length - 1}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function App() {
  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-white font-sans">
      <Hero />
    </div>
  );
}